import { useState, useEffect, useCallback } from 'react';
import { getUserSessions, deleteSession } from '../../../services/chatService';
import { Session } from '../../../types/chat';

/**
 * Interface para conversas agrupadas por data
 */
export interface GroupedConversations {
  label: string;
  conversations: Session[];
}

/**
 * Hook para gerenciar lista de conversas
 *
 * Features:
 * - Paginação com infinite scroll
 * - Busca por título
 * - Agrupamento por data (Hoje, Ontem, Esta Semana, etc.)
 * - Delete com confirmação
 *
 * @returns Estado e funções para gerenciar conversas
 */
export const useConversations = () => {
  const [conversations, setConversations] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const pageSize = 20;

  /**
   * Fetch conversations from API
   */
  const fetchConversations = useCallback(async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setOffset(0);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const currentOffset = reset ? 0 : offset;
      // Include projects to show project badge on conversation cards
      const response = await getUserSessions(pageSize, currentOffset, true);

      if (response.success && Array.isArray(response.data)) {
        if (reset) {
          setConversations(response.data);
        } else {
          setConversations(prev => [...prev, ...response.data]);
        }

        // Check if there are more items
        setHasMore(response.metadata?.has_more ?? response.data.length === pageSize);
        setOffset(currentOffset + pageSize);
      }
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Erro ao carregar conversas';
      setError(errorMessage);
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [offset, pageSize]);

  /**
   * Load more conversations (infinite scroll)
   */
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchConversations(false);
    }
  }, [loadingMore, hasMore, fetchConversations]);

  /**
   * Refresh conversations (reset pagination)
   */
  const refresh = useCallback(() => {
    fetchConversations(true);
  }, [fetchConversations]);

  /**
   * Delete a conversation
   */
  const deleteConversation = useCallback(async (sessionId: number): Promise<void> => {
    try {
      await deleteSession(sessionId);
      setConversations(prev => prev.filter(c => c.id !== sessionId));
    } catch (err) {
      console.error('Error deleting conversation:', err);
      throw err;
    }
  }, []);

  /**
   * Filter conversations by search query
   */
  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return conv.chat_title?.toLowerCase().includes(query);
  });

  /**
   * Group conversations by date
   */
  const groupedConversations = useCallback((): GroupedConversations[] => {
    const groups: { [key: string]: Session[] } = {
      'HOJE': [],
      'ONTEM': [],
      'ESTA SEMANA': [],
      'ESTE MÊS': [],
      'ANTERIORES': [],
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    filteredConversations.forEach(conv => {
      const convDate = new Date(conv.last_access || conv.created_at);
      const convDay = new Date(convDate.getFullYear(), convDate.getMonth(), convDate.getDate());

      if (convDay.getTime() === today.getTime()) {
        groups['HOJE'].push(conv);
      } else if (convDay.getTime() === yesterday.getTime()) {
        groups['ONTEM'].push(conv);
      } else if (convDay >= weekAgo) {
        groups['ESTA SEMANA'].push(conv);
      } else if (convDay >= monthAgo) {
        groups['ESTE MÊS'].push(conv);
      } else {
        groups['ANTERIORES'].push(conv);
      }
    });

    // Return only groups that have conversations
    return Object.entries(groups)
      .filter(([_, convs]) => convs.length > 0)
      .map(([label, convs]) => ({
        label,
        conversations: convs.sort((a, b) =>
          new Date(b.last_access || b.created_at).getTime() -
          new Date(a.last_access || a.created_at).getTime()
        ),
      }));
  }, [filteredConversations]);

  // Initial fetch
  useEffect(() => {
    fetchConversations(true);
  }, []);

  return {
    conversations: filteredConversations,
    groupedConversations: groupedConversations(),
    loading,
    loadingMore,
    error,
    hasMore,
    searchQuery,
    setSearchQuery,
    loadMore,
    refresh,
    deleteConversation,
  };
};
